import { FC, useCallback, useMemo } from 'react'
import styles from 'components/features/socialFeed/FeedPostsPage/FeedPostsPage.styl'
import cn from 'classnames'
import { SingleColumnLayout, FeedExternalLayout } from 'components/layouts/Layouts'
import Anchor from 'components/atoms/navigation/Anchor'
import { PageHeading } from 'components/atoms/Text'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import { FeedPeerGroupsHeaderContainer } from 'components/features/socialFeed/FeedPeerGroupsHeader/FeedPeerGroupsHeaderContainer'
import FeedPostListContainer from 'components/features/socialFeed/FeedPostList/FeedPostListContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { FeedDropZone } from 'components/features/socialFeed/FeedDropZone/FeedDropZone'
import { FeedFilters } from 'components/features/socialFeed/FeedFilters/FeedFilters'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import { PostCompose } from 'components/features/pfm/PostCompose/PostCompose'
import { FeedfilterPopupPlace } from 'components/features/socialFeed/FeedfilterPopupPlace/FeedfilterPopupPlace'
import { DesktopInternalStickyHeader } from 'components/features/pfm/DesktopInternalStickyHeader/DesktopInternalStickyHeader'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { viewportSegmentSelector, isMobileSelector } from 'selectors/viewportSelectors'
import ModuleWrapper from 'components/features/pfm/ModuleWrapper/ModuleWrapper'
import { FeedTrendingTags } from 'components/features/socialFeed/FeedTrendingTags/FeedTrendingTags'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { InternalLayoutOwnProps } from 'components/features/pfm/InternalLayout/InternalLayout'
import { StrippedFragment } from 'components/features/pfm/StrippedFragment/StrippedFragment'
import { PostListModuleHeader } from 'components/features/pfm/PostListModuleHeader/PostListModuleHeader'

const getInternalLayout = (internalLayoutProps: InternalLayoutOwnProps): FC =>
  ({ children }) => (
    <InternalLayoutContainer { ...internalLayoutProps } >
      { children }
    </InternalLayoutContainer>
  )

type FeedPostsPageProps = {

}

export const FeedPostsPageInner: FC<FeedPostsPageProps> = () => {
  const viewportSegment = useStoreSelector(viewportSegmentSelector)


  const renderGroups = useCallback(() => <FeedFilters />, [])
  const renderGroupsPopup = useCallback(() => <FeedfilterPopupPlace />, [])

  const notificationsChild = (
    <NotificationTrayWrapper
      renderLevel='page'
      placement='feedposts_general'
      isPositionRelative
    />
  )

  const sidebarChild = (
    <InternalSidebarContainer
      renderGroups={ renderGroups }
      renderGroupsPopup={ renderGroupsPopup }
    />
  )

  const headerChild = viewportSegment !== 'desktop'
    ? <FeedPeerGroupsHeaderContainer />
    : undefined

  const isMobile = useStoreSelector(isMobileSelector)

  const isAuthenticated = useStoreSelector(isAuthenticatedSelector)

  const trendingTagsChild = useMemo(() => {
    return (
      <ModuleWrapper
        title={ isMobile ? undefined : 'Trending Tags' }
        key='trending-tags'
        forceRoundedCorners
        shadowTheme='medium'
      >
        {isAuthenticated && <FeedTrendingTags />}
      </ModuleWrapper>
    )
  }, [isMobile, isAuthenticated])

  const desktopRightColumn = useMemo(() => {
    if (isMobile) {
      return null
    }

    return (
      <div className={ cn(styles.column, styles.right) }>
        { trendingTagsChild }
      </div>
    )
  }, [isMobile, trendingTagsChild])

  const mobileTrendingTags = useMemo(() => {
    if (!isMobile) {
      return null
    }

    return trendingTagsChild
  }, [isMobile, trendingTagsChild])

  const additionalBlocks = useMemo(() => ({
    'trending-tags': mobileTrendingTags,
  }), [mobileTrendingTags])

  const internalLayoutProps: InternalLayoutOwnProps = {
    sidebar: sidebarChild,
    stickyHeader: headerChild,
    notifications: notificationsChild,
  }

  const Layout = isAuthenticated
    ? getInternalLayout(internalLayoutProps)
    : FeedExternalLayout

  const DropzoneOrNot = isAuthenticated ? FeedDropZone : StrippedFragment

  const feedChild = (
    <DropzoneOrNot>
      {
        isAuthenticated && (
          <Anchor placement='feedposts_compose'>
            <ModuleWrapper
              title='Create a Post'
              card={ false }
            >
              <PostCompose />
            </ModuleWrapper>
          </Anchor>
        )
      }
      <PostListModuleHeader />
      <FeedPostListContainer
        additionalBlocks={ additionalBlocks }
      />
    </DropzoneOrNot>
  )
  const columnsClassName = cn(styles.columnsWrapper, {
    [styles.columnsWrapperAuthenticated]: isAuthenticated,
  })
  const headerClassName = cn(styles.header, {
    [styles.headerIsAuthenticated]: isAuthenticated,
  })

  const isShown = useCallback(() => true, [])

  return (
    <Anchor placement='feedposts'>
      <Layout>
        <Anchor placement='feedtop'>
          {
            isAuthenticated && (
              <DesktopInternalStickyHeader isShown={ isShown } noAnimation>
                <FeedPeerGroupsHeaderContainer />
              </DesktopInternalStickyHeader>
            )
          }
          <SingleColumnLayout>
            <div className={ headerClassName }>
              <PageHeading>Advice Feed</PageHeading>
            </div>
            <div className={ columnsClassName }>
              <div className={ cn(styles.column, styles.left) }>
                { feedChild }
              </div>
              { isAuthenticated && desktopRightColumn }
            </div>
          </SingleColumnLayout>
        </Anchor>
      </Layout>
    </Anchor>
  )
}
